/**
 * Module summary:
 * This page builds the final assessment report view. It loads one assessment,
 * merges in audit activity and assessment-definition metadata, derives findings
 * and recommendations, and presents a print-friendly report layout.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../layout/AppShell';
import {
  REPORT_TEMPLATE_VERSION,
  createFollowUpAssessment,
  getAssessmentAuditTrailFromFirestore,
  getAssessmentById,
  getAssessmentByIdWithRemoteFallback,
  hydrateAssessmentPhotoUrls,
} from '../../services/data';
import type { AssessmentRecord } from '../../services/data';
import { useAuth } from '../../hooks/useAuth';
import { useAssessment } from '../../hooks/useAssessment';
import { responseCreatesFinding } from '../../utils/assessmentScoring';
import type { Question } from '../../types/assessment';
import './pages.css';

function getRiskLevel(score: number) {
  // Translate the numeric score into a human-readable risk tier used in the report.
  if (score >= 85) return { label: 'Low', color: '#0f766e' };
  if (score >= 70) return { label: 'Moderate', color: '#b45309' };
  return { label: 'High', color: '#b91c1c' };
}

function isAnsweredValue(value: any): boolean {
  // Normalize different response types into one answered/not-answered check for filtering and reporting.
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function toCategoryCode(category?: string): string {
  if (!category) return 'GEN';
  // Build a short stable code from the category words so finding ids stay readable in the report.
  const parts = category
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part.charAt(0).toUpperCase());
  return (parts.join('') || 'GEN').padEnd(3, 'X').slice(0, 3);
}

function formatFindingId(category: string, index: number): string {
  const num = String(index + 1).padStart(3, '0');
  return `${toCategoryCode(category)}-${num}`;
}

function buildRecommendations(totalFindings: number, riskLabel: string): string[] {
  const shared = [
    'Implement a prioritized remediation plan with accountable owners and target dates.',
    'Validate closure evidence (photo, documentation, and procedural updates) prior to marking items complete.',
    'Schedule a follow-up verification assessment within 30 to 60 days.',
  ];
  if (riskLabel === 'High') {
    // Escalate the guidance when the score indicates high risk so the report emphasizes urgent action.
    return [
      'Remediate high-risk physical security gaps immediately and apply temporary compensating controls where needed.',
      'Increase supervisory walkthrough cadence until high-risk items are closed.',
      ...shared,
    ];
  }
  if (riskLabel === 'Moderate') {
    return [
      'Close moderate-risk control gaps this quarter with weekly status checkpoints.',
      'Standardize procedures and refresher training for inconsistent control execution.',
      ...shared,
    ];
  }
  return [
    'Maintain current control posture and continue preventive audit cadence.',
    `Track and close the ${totalFindings} identified issue(s) with evidence-based signoff.`,
    ...shared,
  ];
}

export const ReportView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const reportRef = useRef<HTMLDivElement>(null);
  const [assessment, setAssessment] = useState<AssessmentRecord | null>(null);
  const [auditRows, setAuditRows] = useState<any[]>([]);
  const [showFollowUpReview, setShowFollowUpReview] = useState(false);
  const [followUpDraft, setFollowUpDraft] = useState({ name: '', address: '', buildingType: '' });

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setAssessment(null);
      return () => {
        cancelled = true;
      };
    }

    const local = getAssessmentById(id);
    if (local && (!user || !local.userId || local.userId === user.id)) {
      // Show the local record immediately when it belongs to the current user so the report page paints faster.
      setAssessment(local);
      void hydrateAssessmentPhotoUrls(local).then((hydrated) => {
        if (!cancelled) setAssessment(hydrated);
      });
    } else if (local) {
      setAssessment(null);
    }

    void getAssessmentByIdWithRemoteFallback(id).then((record) => {
      if (cancelled) return;
      if (!record) {
        if (!local) setAssessment(null);
        return;
      }
      // Reject records owned by a different user so the browser does not render someone else's report.
      if (user && record.userId && record.userId !== user.id) {
        setAssessment(null);
        return;
      }
      void hydrateAssessmentPhotoUrls(record).then((hydrated) => {
        if (!cancelled) setAssessment(hydrated);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [id, user]);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setAuditRows([]);
      return () => {
        cancelled = true;
      };
    }
    void getAssessmentAuditTrailFromFirestore(id).then((rows) => {
      if (cancelled) return;
      setAuditRows(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const { assessment: assessmentDef } = useAssessment(assessment?.assessmentId ?? null);

  const questionMetaMap = useMemo(() => {
    const map: Record<string, { text: string; category: string; question: Question }> = {};
    if (!assessmentDef) return map;
    assessmentDef.categories.forEach((cat) => {
      cat.questions.forEach((q) => {
        // Index questions by id so later response rows can quickly recover human-readable text and categories.
        map[q.id] = { text: q.text, category: cat.title, question: q };
      });
    });
    return map;
  }, [assessmentDef]);

  if (!assessment) return null;

  const risk = getRiskLevel(assessment.score ?? 0);
  const date = new Date(assessment.updatedAt || Date.now());
  const dateStr = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const normalizeAnswer = (value: any): string => {
    // Convert raw response values into consistent report text for booleans, lists, and blanks.
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not answered';
    if (value === null || value === undefined || value === '') return 'Not answered';
    return String(value);
  };

  const controlResponses = (() => {
    const responses = assessment.responses || {};
    if (assessmentDef) {
      return assessmentDef.categories.flatMap((category) =>
        category.questions
          .map((question) => ({
            key: question.id,
            category: category.title,
            questionText: question.text,
            question,
            answer: normalizeAnswer(responses[question.id]),
            comment: responses[`${question.id}Comment`],
            rawAnswer: responses[question.id],
          }))
          // Keep only answered questions or commented questions so the report excludes untouched controls.
          .filter((item) => isAnsweredValue(item.rawAnswer) || isAnsweredValue(item.comment))
      );
    }
    return Object.entries(responses)
      .filter(([key]) => !key.endsWith('Comment'))
      .filter(([key, value]) => isAnsweredValue(value) || isAnsweredValue(responses[`${key}Comment`]))
      .map(([key, value]) => ({
        key,
        category: 'General',
        questionText: questionMetaMap[key]?.text || key,
        question: questionMetaMap[key]?.question,
        answer: normalizeAnswer(value),
        comment: responses[`${key}Comment`],
        rawAnswer: value,
      }));
  })();

  const allPhotos: { questionId: string; name: string; imageUrl: string }[] = [];
  if (assessment.photos) {
    Object.entries(assessment.photos).forEach(([questionId, photoList]) => {
      photoList.forEach((photo) => {
        const imageUrl = photo.storageUrl || photo.dataUrl;
        if (!imageUrl) return;
        allPhotos.push({ questionId, name: photo.name, imageUrl });
      });
    });
  }

  const photoFigureMap = allPhotos.reduce<Record<string, string[]>>((acc, photo, idx) => {
    const figure = `Fig-${String(idx + 1).padStart(2, '0')}`;
    const key = photo.questionId || 'general';
    // Group figure labels by question id so findings can reference the right photo evidence later.
    if (!acc[key]) acc[key] = [];
    acc[key].push(figure);
    return acc;
  }, {});

  const responseByQuestionId = controlResponses.reduce<Record<string, string>>((acc, item) => {
    acc[item.key] = item.answer;
    return acc;
  }, {});

  const failedFindings = controlResponses
    .filter((item) => responseCreatesFinding(item.question, item.rawAnswer))
    .map((item, index) => {
      const findingId = formatFindingId(item.category, index);
      const figureRefs = photoFigureMap[item.key]?.join(', ') || '-';
      return {
        findingId,
        category: item.category,
        question: item.questionText,
        response: item.answer,
        figureRefs,
        notes: item.comment || 'No additional notes provided.',
      };
    });

  const findings = { total: failedFindings.length };
  const recommendations = buildRecommendations(findings.total, risk.label);

  const openFollowUpReview = () => {
    setFollowUpDraft({
      name: assessment.name,
      address: assessment.address || '',
      buildingType: assessment.buildingType || '',
    });
    setShowFollowUpReview(true);
  };

  const handleCreateFollowUp = async () => {
    try {
      const next = await createFollowUpAssessment({
        ...assessment,
        userId: user?.id || assessment.userId,
      }, {
        name: followUpDraft.name,
        address: followUpDraft.address,
        addressParts: {
          ...assessment.addressParts,
        },
        buildingType: followUpDraft.buildingType,
      });
      setShowFollowUpReview(false);
      navigate(`/assessment?id=${encodeURIComponent(next.id)}`);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not create the follow-up assessment.');
    }
  };

  const buildingLabel = assessment.buildingType || 'Building / Facility';
  const siteLabel = assessment.address || 'Address not provided';
  const reportId = `TMG-${assessment.id}`;
  const effectiveTemplateVersion = REPORT_TEMPLATE_VERSION;
  const submittedEvent = auditRows.slice().reverse().find((e) => e.action === 'submitted');
  const finalizedAtLabel = submittedEvent?.at
    ? new Date(submittedEvent.at).toLocaleString('en-US')
    : 'Not finalized';

  return (
    <AppShell title="Physical Security Assessment Report" isDashboard={true}>
      <div className="report-page-shell">
        <div className="report-page-shell__actions no-print">
          <button onClick={() => navigate('/reports')} className="report-action">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            Back to Reports
          </button>
          <button onClick={openFollowUpReview} className="report-action">
            Create Follow-Up
          </button>
          <button onClick={() => window.print()} className="report-action report-action--primary">
            Export as PDF
          </button>
        </div>

        <div ref={reportRef} className="report-document report-document--watermarked">
          <article className="report-sheet">
            <div className="report-print-header">
              <span>{reportId}</span>
              <span>{`${assessment.name} - ${dateStr}`}</span>
              <span>{date.toISOString().split('T')[0]}</span>
            </div>

            <section className="report-cover-page">
              <div className="report-header">
                <div className="report-header__logo">
                  <img src="/Transparent-Logo.png" alt="The Macha Group" className="report-header__logo-img" />
                </div>
                <h1 className="report-header__title">Physical Security Assessment Report</h1>
                <div className="report-header__line" />
                <p className="report-classification">Confidential - Security Sensitive Information</p>
              </div>

              <section className="report-cover-grid">
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Client / Site</span>
                  <strong className="report-cover-card__value">{assessment.name}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Building Type</span>
                  <strong className="report-cover-card__value">{buildingLabel}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Address</span>
                  <strong className="report-cover-card__value">{siteLabel}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Report ID</span>
                  <strong className="report-cover-card__value">{reportId}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Prepared By</span>
                  <strong className="report-cover-card__value">{user?.displayName || 'The Macha Group'}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Issue Date</span>
                  <strong className="report-cover-card__value">{dateStr}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Template Version</span>
                  <strong className="report-cover-card__value">{effectiveTemplateVersion}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Finalized At</span>
                  <strong className="report-cover-card__value">{finalizedAtLabel}</strong>
                </div>
              </section>

              <div className="report-cover-note">
                This document contains sensitive physical security information and is intended only for authorized stakeholders.
              </div>

              <section className="report-section">
                <h2 className="report-section__title">Table of Contents</h2>
                <ol className="report-toc">
                  <li>1. Executive Summary</li>
                  <li>2. Assessment Scope</li>
                  <li>3. Methodology and Risk Matrix</li>
                  <li>4. Findings Register</li>
                  <li>5. Control Response Register (Answered Questions)</li>
                  <li>6. Recommendations and Action Plan</li>
                  <li>7. Photo Appendix</li>
                  <li>8. Document Control & Audit Trail</li>
                </ol>
              </section>
            </section>
          </article>

          <article className="report-sheet report-page-break">
            <section className="report-section">
              <h2 className="report-section__title">1. Executive Summary</h2>
              <div className="report-findings-grid">
                <div className="report-finding-box">
                  <span className="report-finding-box__label">Total Findings</span>
                  <span className="report-finding-box__count">{findings.total}</span>
                </div>
                <div className="report-finding-box">
                  <span className="report-finding-box__label">Risk Rating</span>
                  <span className="report-finding-box__count" style={{ color: risk.color }}>{risk.label}</span>
                </div>
                <div className="report-finding-box">
                  <span className="report-finding-box__label">Assessment Score</span>
                  <span className="report-finding-box__count">{assessment.score ?? 0}%</span>
                </div>
              </div>
              <p className="report-body-text">
                This report summarizes a physical security assessment for {buildingLabel.toLowerCase()} operations at {siteLabel}. The objective was to identify control gaps, assign practical risk context, and provide a prioritized remediation roadmap.
              </p>
            </section>

            <section className="report-section">
              <h2 className="report-section__title">2. Assessment Scope</h2>
              <div className="report-scope-grid">
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Reported By</span>
                  <strong className="report-cover-card__value">{user?.displayName || 'The Macha Group'}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Assessment Name</span>
                  <strong className="report-cover-card__value">{assessment.name}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Facility Type</span>
                  <strong className="report-cover-card__value">{buildingLabel}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Facility Address</span>
                  <strong className="report-cover-card__value">{siteLabel}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Date of Report</span>
                  <strong className="report-cover-card__value">{dateStr}</strong>
                </div>
                <div className="report-cover-card">
                  <span className="report-cover-card__label">Report ID</span>
                  <strong className="report-cover-card__value">{reportId}</strong>
                </div>
              </div>
            </section>

            <section className="report-section">
              <h2 className="report-section__title">3. Methodology and Risk Matrix</h2>
              <p className="report-body-text">
                Controls were evaluated through on-site walkthrough, visual verification, and assessor response capture. Risk rating reflects observed control effectiveness and potential impact.
              </p>
              <table className="report-register-table">
                <thead>
                  <tr>
                    <th>Risk Level</th>
                    <th>Score Band</th>
                    <th>Interpretation</th>
                    <th>Recommended Closure Window</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>High</td>
                    <td>&lt; 70%</td>
                    <td>Critical control weaknesses with elevated exposure.</td>
                    <td>Immediate to 14 days</td>
                  </tr>
                  <tr>
                    <td>Moderate</td>
                    <td>70% - 84%</td>
                    <td>Notable gaps requiring scheduled remediation.</td>
                    <td>15 to 45 days</td>
                  </tr>
                  <tr>
                    <td>Low</td>
                    <td>&gt;= 85%</td>
                    <td>Generally effective controls with minor improvements.</td>
                    <td>46 to 90 days</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </article>

          <article className="report-sheet">
            <section className="report-section">
              <h2 className="report-section__title">4. Findings Register</h2>
              {failedFindings.length === 0 ? (
                <p className="report-body-text">No failed controls were identified in this submission.</p>
              ) : (
                <table className="report-register-table report-register-table--findings">
                  <thead>
                    <tr>
                      <th>Finding ID</th>
                      <th>Category</th>
                      <th>Question</th>
                      <th>Response</th>
                      <th>Photo Ref</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedFindings.map((finding) => (
                      <tr key={finding.findingId}>
                        <td>{finding.findingId}</td>
                        <td>{finding.category}</td>
                        <td>{finding.question}</td>
                        <td>{finding.response}</td>
                        <td>{finding.figureRefs}</td>
                        <td>{finding.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </article>

          <article className="report-sheet">
            <section className="report-section">
              <h2 className="report-section__title">5. Control Response Register (Answered Questions)</h2>
              {controlResponses.length === 0 ? (
                <p className="report-body-text">No answered controls were captured for this assessment.</p>
              ) : (
                <table className="report-register-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Control Description</th>
                      <th>Response</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {controlResponses.map((item) => (
                      <tr key={item.key}>
                        <td>{item.category}</td>
                        <td>{item.questionText}</td>
                        <td>{item.answer}</td>
                        <td>{item.comment || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="report-section">
              <h2 className="report-section__title">6. Recommendations and Action Plan</h2>
              <ol className="report-ordered-list">
                {recommendations.map((recommendation) => (
                  <li key={recommendation}>{recommendation}</li>
                ))}
              </ol>
            </section>
          </article>

          {allPhotos.length > 0 && (
            <article className="report-sheet">
              <section className="report-section">
                <h2 className="report-section__title">7. Photo Appendix</h2>
                <table className="report-register-table report-appendix-index">
                  <thead>
                    <tr>
                      <th>Figure</th>
                      <th>Description</th>
                      <th>Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPhotos.map((photo, idx) => (
                      <tr key={`index-${idx}`}>
                        <td>{`Fig-${String(idx + 1).padStart(2, '0')}`}</td>
                        <td>{questionMetaMap[photo.questionId]?.text || photo.questionId}</td>
                        <td>{responseByQuestionId[photo.questionId] || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className={`report-photos-grid ${allPhotos.length > 8 ? 'report-photos-grid--compact' : ''}`}>
                  {allPhotos.map((photo, idx) => (
                    <figure key={idx} className="report-photo-item">
                      <img src={photo.imageUrl} alt={photo.name} className="report-photo-item__img" />
                      <figcaption className="report-photo-item__caption">
                        {`Fig-${String(idx + 1).padStart(2, '0')}`}: {questionMetaMap[photo.questionId]?.text || photo.questionId}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </section>
            </article>
          )}

          <article className="report-sheet">
            <section className="report-section">
              <h2 className="report-section__title">8. Document Control & Audit Trail</h2>
              <table className="report-register-table">
                <thead>
                  <tr>
                    <th>Event Time</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Status Transition</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.length === 0 ? (
                    <tr>
                      <td colSpan={5}>No audit events captured.</td>
                    </tr>
                  ) : (
                    auditRows.map((event) => (
                      <tr key={event.id}>
                        <td>{new Date(event.at).toLocaleString('en-US')}</td>
                        <td>{event.action}</td>
                        <td>{event.actorUserId || '-'}</td>
                        <td>{`${event.fromStatus || '-'} -> ${event.toStatus || '-'}`}</td>
                        <td>{event.details || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </section>
          </article>

          <article className="report-sheet report-sheet--footer">
            <footer className="report-footer">
              <p>Copyright {new Date().getFullYear()} The Macha Group. All rights reserved. TM THE MACHA GROUP.</p>
              <p>This report is confidential and intended solely for authorized recipients.</p>
            </footer>
          </article>
        </div>
      </div>
      {showFollowUpReview && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-card">
            <h2 className="content-card__title">Review Follow-Up</h2>
            <p className="content-card__copy">The follow-up keeps the same name by default. Adjust any details before starting if needed.</p>
            <label className="contact-field">
              <span>Name</span>
              <input value={followUpDraft.name} onChange={(e) => setFollowUpDraft((prev) => ({ ...prev, name: e.target.value }))} />
            </label>
            <label className="contact-field">
              <span>Address</span>
              <input value={followUpDraft.address} onChange={(e) => setFollowUpDraft((prev) => ({ ...prev, address: e.target.value }))} />
            </label>
            <label className="contact-field">
              <span>Building Type</span>
              <input value={followUpDraft.buildingType} onChange={(e) => setFollowUpDraft((prev) => ({ ...prev, buildingType: e.target.value }))} />
            </label>
            <div className="profile-form__actions">
              <button type="button" className="report-row__button" onClick={() => setShowFollowUpReview(false)}>
                Cancel
              </button>
              <button type="button" className="dashboard-btn dashboard-btn--solid" onClick={() => void handleCreateFollowUp()}>
                Start Follow-Up
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default ReportView;
